import { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import PayoffTargetControls from './payoffTargetControls'
import { useSelector } from 'react-redux'
import { selectNearestExpiry, selectTargetValues } from '../../selectors/strategyBuilder'
import { useDispatch } from 'react-redux'
import { setPayoffGraphData, updateTargetValues } from '../../reducer/strategyBuilder.reducer'
import { toDayStart } from '../../util/dateTime'
import useSelectPrimaryOrUnderlyingSecurity from '../../hooks/useSelectPrimaryOrUnderlyingSecurity'
import { getSecuritySnapshot } from '../../selectors/security'
import { selectUnderlyingOptionsTradeData } from '../../selectors/options'
import store from '../../store'

export type Props = {
    ivMultiplier: number,
    onIvMultiplierChange: (newIvMultiplier: number) => void
    onSpotTargetPriceChange: (price: number) => void
    spotBasePrice: number,
    numberOfDaysToExpire: number,
    targetDaysFromToday: number,
    numberOfDaysToExpireArray: number[],
    setTargetDaysFromToday: (days: number) => void,
    profit?: number,
    handleResetParams: ()=>void
}

const LivePayoffTargetControlsContainer = () => {
    const dispatch = useDispatch();
    const { primaryOrUnderlying: underlying, primaryOrUnderlyingToken: underlyingToken } = useSelectPrimaryOrUnderlyingSecurity();
    const {ivMultiplier, spotTargetPrice, spotBasePrice, targetDate} = useSelector(selectTargetValues);
    const nearestExpiry = useSelector(selectNearestExpiry);

    const priceMultiplier = underlying?.priceMultiplier ?? 100

    const readOptionsTradeData = useCallback(() => {
        return selectUnderlyingOptionsTradeData(underlyingToken)(store.getState())
    }, [underlyingToken])

    const { minStrike, maxStrike } = useMemo(() => {
        const optionsTradesData = readOptionsTradeData()
        if (!optionsTradesData || optionsTradesData.length === 0) {
            return { minStrike: 0, maxStrike: Infinity }
        }
        
        const nearestExpiryData = optionsTradesData.find(data => data.expiration?.expiry?.expiry === nearestExpiry)
        if (!nearestExpiryData || !nearestExpiryData.trades || nearestExpiryData.trades.length === 0) {
            return { minStrike: 0, maxStrike: Infinity }
        }

        const strikes = nearestExpiryData.trades.map(t => t.strikeCol.strike / priceMultiplier)
        const min = Math.min(...strikes)
        const max = Math.max(...strikes)
        
        return { minStrike: min, maxStrike: max }
    }, [readOptionsTradeData, nearestExpiry, priceMultiplier])

    const tickSize = useMemo(() => {
        return (underlying?.tickSize ?? 5) / priceMultiplier
    }, [underlying?.tickSize, priceMultiplier])

    const hasInitializedPriceRef = useRef(false)
    const prevUnderlyingTokenRef = useRef(underlyingToken)

    useEffect(() => {
        const isTokenValid = underlyingToken && !underlyingToken.startsWith('EXCHANGE_UNKNOWN') && underlyingToken.includes(':')
        if (!isTokenValid) {
            if (prevUnderlyingTokenRef.current && !prevUnderlyingTokenRef.current.startsWith('EXCHANGE_UNKNOWN')) {
                hasInitializedPriceRef.current = false
            }
            return
        }

        if (prevUnderlyingTokenRef.current !== underlyingToken) {
            prevUnderlyingTokenRef.current = underlyingToken
            hasInitializedPriceRef.current = false
        }

        if (!spotTargetPrice && !spotBasePrice) {
            hasInitializedPriceRef.current = false
        }

        if (hasInitializedPriceRef.current) return
        if (spotTargetPrice && spotBasePrice) return

        const tryInitFromSnapshot = () => {
            if (hasInitializedPriceRef.current) return

            const { spotTargetPrice: currentSpotTarget, spotBasePrice: currentSpotBase } = selectTargetValues(store.getState())
            if (currentSpotTarget && currentSpotBase) return

            const ltp = getSecuritySnapshot(underlyingToken)?.lastTradedPrice
            if (typeof ltp === 'number' && ltp > 0) {
                hasInitializedPriceRef.current = true
                dispatch(updateTargetValues({ spotBasePrice: ltp, spotTargetPrice: ltp }))
            }
        }

        tryInitFromSnapshot()
        if (hasInitializedPriceRef.current) return

        return store.subscribe(tryInitFromSnapshot)
    }, [underlyingToken, spotTargetPrice, spotBasePrice, dispatch])

    const onIvMultiplierChange = useCallback((ivMultiplier: number) => {
        dispatch(updateTargetValues({
            ivMultiplier
        }))
    }, [])

    const onSpotTargetPriceChange = useCallback((newSpotTargetPrice: number) => {
        dispatch(updateTargetValues({spotTargetPrice: newSpotTargetPrice * priceMultiplier}))
    }, [priceMultiplier])
    
    const setTargetDate = useCallback((targetDate: number) => {
        dispatch(updateTargetValues({targetDate}))
    }, [])
    

    const handleResetParams = useCallback(() => {
        dispatch(setPayoffGraphData({
            optionsTradesData: readOptionsTradeData()
        }))
        const nowDate = new Date()
        nowDate.setHours(0, 0, 0, 0)
        const ltp = getSecuritySnapshot(underlyingToken)?.lastTradedPrice
        dispatch(updateTargetValues({
            spotBasePrice: ltp,
            spotTargetPrice: ltp,
            ivMultiplier: 1,
            targetDate: nowDate.getTime()
        }))
    }, [readOptionsTradeData, underlyingToken, dispatch])
    return (
        <PayoffTargetControls
            ivMultiplier={ivMultiplier}
            nearestExpiry={nearestExpiry}
            onIvMultiplierChange={onIvMultiplierChange}
            spotTargetPrice={spotTargetPrice / priceMultiplier}
            onSpotTargetPriceChange={onSpotTargetPriceChange}
            baseTimestamp={toDayStart(Date.now()).getTime()}
            targetTimestamp={targetDate}
            handleResetParams={handleResetParams}
            setTargetDate={setTargetDate}
            tickSize={tickSize}
            minStrike={minStrike}
            maxStrike={maxStrike}
        />
    )
}

export default memo(LivePayoffTargetControlsContainer)
